<script setup>
import { reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistant: { type: Object, default: null },
  agentModeAvailable: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['save', 'cancel']);
const { t } = useI18n();

const form = reactive({
  name: '',
  description: '',
  instructions: '',
  enabled: true,
  mode: 'classic',
});

watch(
  () => props.assistant,
  value => {
    Object.assign(form, {
      name: value?.name || '',
      description: value?.description || '',
      instructions: value?.instructions || '',
      enabled: value?.enabled ?? true,
      mode: value?.mode || 'classic',
    });
  },
  { immediate: true }
);

watch(
  () => props.agentModeAvailable,
  available => {
    if (!available) form.mode = 'classic';
  }
);

const submit = () => {
  emit('save', {
    name: form.name.trim(),
    description: form.description.trim(),
    instructions: form.instructions.trim(),
    enabled: form.enabled,
    mode: form.mode,
  });
};
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="submit">
    <div class="grid gap-4 md:grid-cols-2">
      <Input
        v-model="form.name"
        :label="t('JUYANG_AI.ASSISTANT.NAME')"
        maxlength="120"
        required
      />
      <div class="flex items-end justify-between gap-4 pb-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('JUYANG_AI.ENABLED') }}
        </span>
        <Switch v-model="form.enabled" />
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('JUYANG_AI.ASSISTANT.MODE') }}
      </span>
      <div class="inline-flex w-fit gap-1 rounded-lg bg-n-alpha-2 p-1">
        <button
          type="button"
          class="h-8 rounded-md px-3 text-sm"
          :class="
            form.mode === 'classic'
              ? 'bg-n-background font-medium text-n-slate-12 shadow-sm'
              : 'text-n-slate-11'
          "
          @click="form.mode = 'classic'"
        >
          {{ t('JUYANG_AI.ASSISTANT.MODE_CLASSIC') }}
        </button>
        <button
          type="button"
          class="h-8 rounded-md px-3 text-sm"
          :class="
            form.mode === 'agent'
              ? 'bg-n-background font-medium text-n-slate-12 shadow-sm'
              : 'text-n-slate-11'
          "
          :disabled="!agentModeAvailable"
          @click="form.mode = 'agent'"
        >
          {{ t('JUYANG_AI.ASSISTANT.MODE_AGENT') }}
        </button>
      </div>
      <p class="m-0 text-xs text-n-slate-10">
        {{
          form.mode === 'agent'
            ? t('JUYANG_AI.ASSISTANT.MODE_AGENT_DESCRIPTION')
            : t('JUYANG_AI.ASSISTANT.MODE_CLASSIC_DESCRIPTION')
        }}
      </p>
    </div>

    <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
      {{ t('JUYANG_AI.ASSISTANT.DESCRIPTION') }}
      <textarea
        v-model="form.description"
        class="reset-base min-h-24 w-full resize-y rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
        maxlength="500"
        required
      />
    </label>

    <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
      {{ t('JUYANG_AI.ASSISTANT.INSTRUCTIONS') }}
      <textarea
        v-model="form.instructions"
        class="reset-base min-h-40 w-full resize-y rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
        :placeholder="t('JUYANG_AI.ASSISTANT.INSTRUCTIONS_PLACEHOLDER')"
      />
    </label>

    <div class="flex justify-end gap-2">
      <Button
        v-if="!assistant"
        type="button"
        color="slate"
        variant="ghost"
        :label="t('JUYANG_AI.CANCEL')"
        @click="emit('cancel')"
      />
      <Button
        type="submit"
        icon="i-lucide-save"
        :label="isSaving ? t('JUYANG_AI.SAVING') : t('JUYANG_AI.SAVE')"
        :is-loading="isSaving"
        :disabled="isSaving"
      />
    </div>
  </form>
</template>
